import type {
  TrackingEventName,
  TrackingPayload,
} from '@src/Providers/Tracking/TrackingContext';

type EmitFn = (
  event: TrackingEventName,
  props?: TrackingPayload,
) => void | Promise<void>;
type FlushFn = () => void | Promise<void>;

/**
 * Module bridge so non-React code (RTK endpoints, plain utils) AND components
 * that render in lazy-loaded route chunks can emit canonical events.
 *
 * CRITICAL: the registered functions live on `window`, NOT in module-level
 * variables. Webpack/Gatsby duplicate this module into several lazy route
 * chunks; a module-level `registeredEmit` would then exist as a separate null
 * per copy, and only the main chunk's copy is ever registered by the provider.
 * trackEvent() called from a lazy chunk (News player, bookmarks, paywall,
 * checkout) would read its own null and silently drop the event. `window` is a
 * single object shared across every chunk, so all copies see the same
 * emit/flush the provider registered once.
 */
interface TraceTrackingBridge {
  emit?: EmitFn | null;
  flush?: FlushFn | null;
}

const getBridge = (): TraceTrackingBridge => {
  if (typeof window === 'undefined') return {};
  const w = window as unknown as {__traceTrackingBridge?: TraceTrackingBridge};
  if (!w.__traceTrackingBridge) w.__traceTrackingBridge = {};
  return w.__traceTrackingBridge;
};

/** Registered once by TrackingProvider (see TrackingContext). */
export const registerTrackingEmit = (fn: EmitFn) => {
  getBridge().emit = fn;
};

export const registerTrackingFlush = (fn: FlushFn) => {
  getBridge().flush = fn;
};

/**
 * Emit a canonical event from anywhere (non-React code or a lazy chunk).
 * Routes through the same consent-gated pipeline as useTracking().emit.
 * No-op until the TrackingProvider has mounted (SSR / very early calls drop).
 */
export const trackEvent: EmitFn = (event, props) => {
  return getBridge().emit?.(event, props);
};

/** Force queued events to send now (used before the Stripe redirect). */
export const flushTracking: FlushFn = () => {
  return getBridge().flush?.();
};

export default trackEvent;
